import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/axios';
import Breadcrumb from '../../components/ui/Breadcrumb';

const CaseFileCreate = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [procedureTypes, setProcedureTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    client_id: clientId,
    procedure_type_id: '',
    assigned_user_id: '',
    title: '',
    notes: '',
  });

  useEffect(() => {
    Promise.all([
      api.get('/procedure-types'),
      api.get('/users'),
    ]).then(([ptRes, usersRes]) => {
      setProcedureTypes(ptRes.data);
      setUsers(usersRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: null });

    if (name === 'procedure_type_id') {
      const type = procedureTypes.find(pt => pt.id === parseInt(value));
      setSelectedType(type || null);
      if (type) setForm(prev => ({ ...prev, procedure_type_id: value, title: type.name }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post('/case-files', form);
      navigate(`/case-files/${res.data.id}`);
    } catch (error) {
      if (error.response?.status === 422) setErrors(error.response.data.errors);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="text-center py-5">
      <div className="spinner-border text-primary"></div>
    </div>
  );

  return (
    <div className="container-fluid">
      <Breadcrumb items={[
        { label: 'Clientes', to: '/clients' },
        { label: 'Cliente', to: `/clients/${clientId}` },
        { label: 'Nuevo Expediente' },
      ]} />
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Nuevo Expediente</h4>
        <Link to={`/clients/${clientId}`} className="btn btn-outline-secondary btn-sm">
          <i className="ti-arrow-left mr-1"></i> Regresar
        </Link>
      </div>

      <div className="row">
        {/* Formulario */}
        <div className="col-12 col-md-8">
          <div className="card mb-4">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label>Tipo de Trámite <span className="text-danger">*</span></label>
                      <select name="procedure_type_id"
                        className={`form-control ${errors.procedure_type_id ? 'is-invalid' : ''}`}
                        value={form.procedure_type_id} onChange={handleChange} required>
                        <option value="">Seleccionar...</option>
                        {procedureTypes.map(pt => (
                          <option key={pt.id} value={pt.id}>{pt.name}</option>
                        ))}
                      </select>
                      {errors.procedure_type_id && (
                        <div className="invalid-feedback">{errors.procedure_type_id[0]}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label>Responsable <span className="text-danger">*</span></label>
                      <select name="assigned_user_id"
                        className={`form-control ${errors.assigned_user_id ? 'is-invalid' : ''}`}
                        value={form.assigned_user_id} onChange={handleChange} required>
                        <option value="">Seleccionar...</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                      {errors.assigned_user_id && (
                        <div className="invalid-feedback">{errors.assigned_user_id[0]}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-group">
                      <label>Título <span className="text-danger">*</span></label>
                      <input type="text" name="title"
                        className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                        value={form.title} onChange={handleChange} required />
                      {errors.title && <div className="invalid-feedback">{errors.title[0]}</div>}
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-group">
                      <label>Notas iniciales</label>
                      <textarea name="notes" rows="3" className="form-control"
                        value={form.notes} onChange={handleChange}></textarea>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Creando...' : 'Crear Expediente'}
                  </button>
                  <Link to={`/clients/${clientId}`} className="btn btn-outline-secondary">Cancelar</Link>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Checklist de documentos — debajo del form en móvil */}
        {selectedType && selectedType.document_checklists?.length > 0 && (
          <div className="col-12 col-md-4">
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">Documentos que se cargarán</h6>
              </div>
              <div className="card-body p-0">
                <ul className="list-group list-group-flush">
                  {selectedType.document_checklists.map(doc => (
                    <li key={doc.id} className="list-group-item d-flex justify-content-between align-items-center">
                      <span>{doc.name}</span>
                      {doc.required
                        ? <span className="badge badge-danger">Requerido</span>
                        : <span className="badge badge-light">Opcional</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseFileCreate;